import { DateTime } from 'luxon'

import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal, mergeAndStringifyEntityObjects } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import { mapToState } from '@functions/customDeadline'

import * as customDeadlineActions from '@redux/modules/customDeadline'

import defaultFormState, { requiredFields } from '@models/customDeadline'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { CustomDeadlineModel, CustomDeadlineRequestOptions } from '@models/types'

export const generateUrls = (customDeadline?: Partial<CustomDeadlineModel>, options: { prefixRoute?: string } = {}) => {
  const { id } = customDeadline || {}

  const { prefixRoute = 'systemManager' } = options

  return {
    editCustomDeadlineDetailsUrl: `#/${prefixRoute}/customDeadlines/${id}/edit/details`,
    editCustomDeadlineUrl: `#/${prefixRoute}/customDeadlines/${id}/edit/`,
    customDeadlinesIndexUrl: `#/${prefixRoute}/customDeadlines`,
  }
}

type CreateCustomDeadlineParams = {
  customDeadlineParams: Partial<CustomDeadlineModel>,
  dispatch: AppDispatch,
  requestOptions?: CustomDeadlineRequestOptions,
}

const createCustomDeadline = (params: CreateCustomDeadlineParams) => {
  const { dispatch, customDeadlineParams, requestOptions } = params
  const { deadline_date } = customDeadlineParams

  const { createCustomDeadline: createFn } = customDeadlineActions

  const updatedParams = mergeAndStringifyEntityObjects(
    {},
    {
      ...customDeadlineParams,
      deadline_date: deadline_date ? DateTime.fromFormat(deadline_date, 'yyyy-MM-dd').toFormat('dd/MM/yyyy') : '',
    },
    [],
    ['alert_emails', 'enabled_days'],
  )

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdateCustomDeadlineParams = {
  customDeadline: CustomDeadlineModel,
  customDeadlineParams: Partial<CustomDeadlineModel>,
  dispatch: AppDispatch,
  requestOptions?: CustomDeadlineRequestOptions,
}

const updateCustomDeadline = (params: UpdateCustomDeadlineParams) => {
  const {
    dispatch, customDeadline, customDeadlineParams, requestOptions,
  } = params
  const { deadline_date } = customDeadlineParams

  const { updateCustomDeadline: updateFn } = customDeadlineActions

  const updatedParams = mergeAndStringifyEntityObjects(
    customDeadline,
    {
      ...customDeadlineParams,
      deadline_date: deadline_date ? DateTime.fromFormat(deadline_date, 'yyyy-MM-dd').toFormat('dd/MM/yyyy') : '',
    },
    [],
    ['alert_emails', 'enabled_days'],
  )

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCustomDeadlineParams = {
  customDeadline: DeleteParams<CustomDeadlineModel>,
  dispatch: AppDispatch,
}

const deleteCustomDeadline = (params: DeleteCustomDeadlineParams) => {
  const { dispatch, customDeadline } = params
  const { deleteCustomDeadline: deleteFn } = customDeadlineActions

  return dispatch(deleteFn(customDeadline))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCustomDeadlineForm(
  customDeadline: Partial<CustomDeadlineModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const customDeadlineForm = useForm(
    defaultFormState,
    { entity: mapToState(customDeadline), requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [customDeadline.id, customDeadline.cache_key],
  )

  return {
    ...customDeadlineForm,
  }
}

function useCustomDeadline(initEntity: Partial<CustomDeadlineModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: customDeadline }: { entity: CustomDeadlineModel } = useLatestEntity(initEntity, 'customDeadlines')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.customDeadlines)

  return {
    callbacks: {
      createCustomDeadline:
        (customDeadlineParams: Partial<CustomDeadlineModel>, entityOptions?: CustomDeadlineRequestOptions) => (
          createCustomDeadline({ customDeadlineParams, dispatch, requestOptions: entityOptions })
        ),
      createOrEditCustomDeadline: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCustomDeadlineModal',
        payload: { customDeadline },
      }),
      deleteCustomDeadline: () => deleteCustomDeadline({ dispatch, customDeadline }),
      updateCustomDeadline:
        (customDeadlineParams: Partial<CustomDeadlineModel>, entityOptions?: CustomDeadlineRequestOptions) => (
          updateCustomDeadline({
            customDeadline, customDeadlineParams, dispatch, requestOptions: entityOptions,
          })
        ),
    },
    creating,
    customDeadline,
    deleting,
    loading: loading && !loadedIds.includes(customDeadline.id),
    updating,
  }
}

export default useCustomDeadline
